import { useCallback, useMemo } from 'react';

import {
  GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT,
  IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED,
} from '@/components/constants/flags';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type ImproveNotesTaskId,
  type PostNoteQualityArm,
  type SpotlightDirection,
} from '@/models/analytics/features/post-note-quality';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';

/** Feature tag shared by every post-note quality `button_clicked` dispatch. */
const POST_NOTE_QUALITY_FEATURE = 'post_note_quality';

/**
 * GRO-3284 — single owner of every "Improve your notes" / post-note quality
 * analytics dispatch (event taxonomy agreed with Growth, 2026-06-12).
 *
 * Routing: these events intentionally go to **Amplitude** (experiment metrics)
 * in addition to the default Snowplow + Datadog, so each dispatch passes
 * `options: { amplitude: true }`. This is a deliberate deviation from the
 * Snowplow-only default for new events, signed off with Growth/analytics for
 * the post-note quality experiment. Snowplow Iglu schemas for the net-new
 * events land as a fast-follow.
 *
 * Centralising here keeps the dispatch sites one-liners and the routing
 * decision in exactly one place.
 */
export function usePostNoteQualityAnalytics() {
  const sessionId = useCurrentSessionId();

  // Resolve the arm once so every event can be sliced by experiment arm in
  // Amplitude. Undefined for unenrolled users (events still fire if a surface
  // somehow renders, but in practice these only fire inside the sidebar arms).
  const experimentArm: PostNoteQualityArm | undefined = useMemo(
    () =>
      IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED()
        ? GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT()
        : undefined,
    []
  );

  const trackPanelOpened = useCallback(
    (firstView: boolean) => {
      track({
        event_name: TrackingEventName.PanelOpened,
        props: {
          panel_type: 'improve_notes_panel',
          trigger: 'manual',
          first_view: firstView,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId]
  );

  const trackPanelClosed = useCallback(
    (closeReason: 'collapse' | 'dismiss') => {
      track({
        event_name: TrackingEventName.PanelClosed,
        props: {
          panel_type: 'improve_notes_panel',
          trigger: 'manual',
          close_reason: closeReason,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId]
  );

  const trackTaskCardOpened = useCallback(
    (taskId: ImproveNotesTaskId) => {
      track({
        event_name: TrackingEventName.PanelOpened,
        props: {
          panel_type: 'improve_notes_task_card',
          trigger: 'manual',
          task_id: taskId,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId]
  );

  const trackTaskCardClosed = useCallback(
    (taskId: ImproveNotesTaskId) => {
      track({
        event_name: TrackingEventName.PanelClosed,
        props: {
          panel_type: 'improve_notes_task_card',
          trigger: 'manual',
          task_id: taskId,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId]
  );

  const trackTaskCtaClicked = useCallback(
    (taskId: ImproveNotesTaskId) => {
      // Reuses the existing `button_clicked` event (per the meeting) but
      // dispatched directly rather than via `trackButtonClicked`, because that
      // helper force-disables Amplitude — and these CTA clicks are wanted in
      // Amplitude for the experiment funnel (same pattern as Dictate logs).
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          screen_name: ScreenName.Session,
          action: ButtonAction.PostNoteQualityTaskCtaClicked,
          label: taskId,
          feature: POST_NOTE_QUALITY_FEATURE,
          task_id: taskId,
          session_id: sessionId ?? undefined,
          session_stage: 'post',
        },
        options: { amplitude: true },
      });
    },
    [sessionId]
  );

  const trackTaskCompleted = useCallback(
    (taskId: ImproveNotesTaskId, completedCount: number) => {
      track({
        event_name: TrackingEventName.ImproveNotesTaskCompleted,
        props: {
          task_id: taskId,
          completed_count: completedCount,
          experiment_arm: experimentArm,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId, experimentArm]
  );

  const trackExperienceCompleted = useCallback(
    (tasksCompleted: number) => {
      track({
        event_name: TrackingEventName.PostNoteExperienceCompleted,
        props: {
          tasks_completed: tasksCompleted,
          experiment_arm: experimentArm,
          session_id: sessionId ?? undefined,
        },
        options: { amplitude: true },
      });
    },
    [sessionId, experimentArm]
  );

  const trackTrialDaysUnlocked = useCallback(
    (days: number, tasksCompleted: number) => {
      track({
        event_name: TrackingEventName.TrialDaysUnlocked,
        props: {
          days,
          tasks_completed: tasksCompleted,
          experiment_arm: experimentArm,
        },
        options: { amplitude: true },
      });
    },
    [experimentArm]
  );

  const trackSpotlightAdvanced = useCallback(
    (feature: ImproveNotesTaskId, direction: SpotlightDirection, step?: number) => {
      track({
        event_name: TrackingEventName.SpotlightAdvanced,
        props: { feature, direction, step },
        options: { amplitude: true },
      });
    },
    []
  );

  const trackSpotlightClosed = useCallback(
    (feature: ImproveNotesTaskId, completed: boolean, step?: number) => {
      track({
        event_name: TrackingEventName.SpotlightClosed,
        props: { feature, completed, step },
        options: { amplitude: true },
      });
    },
    []
  );

  return {
    trackPanelOpened,
    trackPanelClosed,
    trackTaskCardOpened,
    trackTaskCardClosed,
    trackTaskCtaClicked,
    trackTaskCompleted,
    trackExperienceCompleted,
    trackTrialDaysUnlocked,
    trackSpotlightAdvanced,
    trackSpotlightClosed,
  };
}
